import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { 
  Plus, Edit, Trash2, Heart, BookOpen, Crown, Sparkles, Calendar, 
  Eye, Download, ChevronDown, ChevronUp, GripVertical, Copy, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';

// Common Catholic prayers that can be imported
const COMMON_PRAYERS = {
  daily: [
    {
      title: { sv: 'Fader Vår' },
      content: { sv: `Fader vår, som är i himmelen.
Helgat varde ditt namn.
Tillkomme ditt rike.
Ske din vilja, såsom i himmelen, så ock på jorden.
Vårt dagliga bröd giv oss idag.
Och förlåt oss våra skulder,
såsom ock vi förlåta dem oss skyldiga äro.
Och inled oss icke i frestelse,
utan fräls oss ifrån ondo.
Amen.` }
    },
    {
      title: { sv: 'Hell dig Maria' },
      content: { sv: `Hell dig Maria, full av nåd,
Herren är med dig.
Välsignad är du bland kvinnor,
och välsignad är din livsfrukt Jesus.
Heliga Maria, Guds moder,
bed för oss syndare,
nu och i vår dödsstund.
Amen.` }
    },
    {
      title: { sv: 'Ära vare Fadern' },
      content: { sv: `Ära vare Fadern och Sonen och den helige Ande,
nu och alltid och i evigheters evighet.
Amen.` }
    },
    {
      title: { sv: 'Korstecknet' },
      content: { sv: `I Faderns och Sonens och den helige Andes namn.
Amen.` }
    },
    {
      title: { sv: 'Trosbekännelsen (Apostoliska)' },
      content: { sv: `Jag tror på Gud Fader allsmäktig, himmelens och jordens skapare.
Jag tror ock på Jesus Kristus, hans enfödde Son, vår Herre,
vilken är avlad av den helige Ande, född av jungfrun Maria,
pinad under Pontius Pilatus, korsfäst, död och begraven,
nederstigen till dödsriket, på tredje dagen uppstånden igen ifrån de döda,
uppstigen till himmelen, sittande på allsmäktig Gud Faders högra sida,
därifrån igenkommande till att döma levande och döda.
Jag tror ock på den helige Ande,
en helig allmännelig kyrka, de heligas samfund,
syndernas förlåtelse, de dödas uppståndelse och ett evigt liv.
Amen.` }
    },
    {
      title: { sv: 'Morgonbön' },
      content: { sv: `Herre, jag tackar dig för denna nya dag.
Hjälp mig att leva den i din kärlek.
Välsigna mina tankar, ord och handlingar.
Led mig på rätt väg och beskydda mig från det onda.
Låt din frid vara med mig genom hela dagen.
Amen.` }
    },
    {
      title: { sv: 'Aftonbön' },
      content: { sv: `Herre, jag tackar dig för denna dag som gått.
Förlåt mig om jag gjort något ont.
Välsigna min vila och ge mig en god natts sömn.
Beskydda mig och mina nära och kära.
I din hand lägger jag min ande.
Amen.` }
    }
  ],
  rosary: [
    {
      title: { sv: 'Rosenkransens Glädjefulla Mysterier' },
      content: { sv: `1. Bebådelsen - Ängeln Gabriel bebådar för Maria att hon ska bli Guds moder
2. Marias besök hos Elisabet - Maria besöker sin släkting Elisabet
3. Jesu födelse - Jesus föds i Betlehem
4. Frambärandet i templet - Maria och Josef bär fram Jesus i templet
5. Återfinnandet av Jesus i templet - Jesus återfinns i templet bland de skriftlärda` }
    },
    {
      title: { sv: 'Rosenkransens Ljusets Mysterier' },
      content: { sv: `1. Jesu dop i Jordan - Jesus döps av Johannes Döparen
2. Bröllopet i Kana - Jesus gör sitt första under
3. Guds rikes förkunnelse - Jesus förkunnar Guds rike och kallar till omvändelse
4. Förklaringen på berget - Jesus förvandlas på berget Tabor
5. Eukaristins instiftande - Jesus instiftar nattvarden vid sista måltiden` }
    },
    {
      title: { sv: 'Rosenkransens Smärtofulla Mysterier' },
      content: { sv: `1. Jesu dödsångest i Getsemane - Jesus ber i örtagården
2. Gisslandet - Jesus gisslas på Pilatus order
3. Törnekronan - Jesus kröns med en törnekrona
4. Korsbärandet - Jesus bär sitt kors till Golgata
5. Korsfästelsen - Jesus korsfästs och dör för våra synder` }
    },
    {
      title: { sv: 'Rosenkransens Ärorika Mysterier' },
      content: { sv: `1. Uppståndelsen - Jesus uppstår från de döda
2. Himmelsfärden - Jesus uppstiger till himmelen
3. Den helige Andes utgjutande - Den helige Ande sänds över apostlarna
4. Marias upptagning i himmelen - Maria upptas med kropp och själ till himmelen
5. Marias kröning i himmelen - Maria kröns som himmelens drottning` }
    },
    {
      title: { sv: 'Hur man ber Rosenkransen' },
      content: { sv: `1. Gör korstecknet och be Trosbekännelsen
2. Be ett Fader vår
3. Be tre Hell dig Maria (för tro, hopp och kärlek)
4. Be Ära vare Fadern
5. Tillkännage första mysteriet och be ett Fader vår
6. Be tio Hell dig Maria medan du mediterar över mysteriet
7. Be Ära vare Fadern
8. (Valfritt: Fatimabön)
9. Tillkännage nästa mysterium och upprepa steg 6-8
10. Efter alla fem mysterier: be Salve Regina
11. Avsluta med korstecknet` }
    }
  ]
};

const PrayersManager = () => {
  const [prayers, setPrayers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingPrayer, setEditingPrayer] = useState(null);
  const [previewPrayer, setPreviewPrayer] = useState(null);
  const [activeTab, setActiveTab] = useState('daily');
  const [expandedCards, setExpandedCards] = useState({});
  const [formData, setFormData] = useState({
    title: { sv: '' },
    content: { sv: '' },
    prayer_type: 'daily',
    date: null,
    order: 0
  });

  const prayerTypes = [
    { value: 'daily', label: 'Dagliga Böner', icon: Heart, color: 'blue', description: 'Grundläggande katolska böner' },
    { value: 'papal', label: 'Påvens Intentioner', icon: Crown, color: 'yellow', description: 'Månatliga böneintentioner' },
    { value: 'rosary', label: 'Rosenkransen', icon: BookOpen, color: 'purple', description: 'Mysterier och vägledning' },
    { value: 'custom', label: 'Övriga Böner', icon: Sparkles, color: 'green', description: 'Egna böner och texter' }
  ];

  const monthNames = [
    'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
    'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
  ];

  const loadPrayers = async () => {
    try {
      const data = await api.getPrayers();
      setPrayers(data);
    } catch (error) {
      console.error('Error loading prayers:', error);
      toast.error('Kunde inte ladda böner');
    }
  };

  useEffect(() => {
    loadPrayers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        date: formData.date ? new Date(formData.date).toISOString() : null
      };

      if (editingPrayer) {
        await api.updatePrayer(editingPrayer.id, submitData);
        toast.success('Bön uppdaterad!');
      } else {
        await api.createPrayer(submitData);
        toast.success('Bön skapad!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadPrayers();
    } catch (error) {
      console.error('Error saving prayer:', error);
      toast.error('Ett fel uppstod');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker på att du vill radera denna bön?')) {
      try {
        await api.deletePrayer(id);
        toast.success('Bön raderad!');
        loadPrayers();
      } catch (error) {
        toast.error('Kunde inte radera bönen');
      }
    }
  };

  const handleImportPrayer = async (prayer, type) => {
    try {
      await api.createPrayer({
        ...prayer,
        prayer_type: type,
        date: null
      });
      toast.success(`"${prayer.title.sv}" importerad!`);
      loadPrayers();
    } catch (error) {
      toast.error('Kunde inte importera bönen');
    }
  };

  const handleImportAll = async (type) => {
    const prayersToImport = COMMON_PRAYERS[type];
    if (!prayersToImport) return;

    let imported = 0;
    for (const prayer of prayersToImport) {
      try {
        await api.createPrayer({
          ...prayer,
          prayer_type: type,
          date: null
        });
        imported++;
      } catch (error) {
        console.error('Error importing prayer:', error);
      }
    }
    toast.success(`${imported} böner importerade!`);
    loadPrayers();
    setIsImportOpen(false);
  };

  const resetForm = () => {
    setFormData({
      title: { sv: '' },
      content: { sv: '' },
      prayer_type: activeTab,
      date: null,
      order: 0
    });
    setEditingPrayer(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setFormData(prev => ({ ...prev, prayer_type: activeTab }));
    setIsDialogOpen(true);
  };

  const openEditDialog = (prayer) => {
    setEditingPrayer(prayer);
    setFormData({
      title: prayer.title || { sv: '' },
      content: prayer.content || { sv: '' },
      prayer_type: prayer.prayer_type,
      date: prayer.date ? new Date(prayer.date).toISOString().split('T')[0] : null,
      order: prayer.order || 0
    });
    setIsDialogOpen(true);
  };

  const openPreview = (prayer) => {
    setPreviewPrayer(prayer);
    setIsPreviewOpen(true);
  };

  const toggleExpanded = (prayerId) => {
    setExpandedCards(prev => ({
      ...prev,
      [prayerId]: !prev[prayerId]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopierat till urklipp!');
  };

  const getFilteredPrayers = (type) => {
    return prayers
      .filter(p => p.prayer_type === type)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const getTypeConfig = (type) => {
    return prayerTypes.find(t => t.value === type) || prayerTypes[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const renderPrayerCard = (prayer) => {
    const config = getTypeConfig(prayer.prayer_type);
    const Icon = config.icon;
    const isExpanded = expandedCards[prayer.id];
    const contentPreview = prayer.content?.sv?.substring(0, 150) || '';
    const hasMoreContent = (prayer.content?.sv?.length || 0) > 150;
    
    return (
      <Card key={prayer.id} className="border-0 shadow-md hover:shadow-lg transition-all">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Drag Handle (future feature) */}
            <div className="mt-1 cursor-grab opacity-30 hover:opacity-60">
              <GripVertical className="w-4 h-4 text-gray-400" />
            </div>
            
            {/* Icon */}
            <div className={`w-10 h-10 rounded-full bg-${config.color}-100 flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 text-${config.color}-600`} />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {prayer.title?.sv || 'Utan titel'}
                  </h3>
                  {prayer.date && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full mb-2">
                      <Calendar className="w-3 h-3" />
                      {formatDate(prayer.date)}
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openPreview(prayer)}
                    title="Förhandsgranska"
                    data-testid={`preview-prayer-${prayer.id}`}
                  >
                    <Eye className="w-4 h-4 text-gray-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(prayer.content?.sv || '')}
                    title="Kopiera text"
                  >
                    <Copy className="w-4 h-4 text-gray-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(prayer)}
                    data-testid={`edit-prayer-${prayer.id}`}
                  >
                    <Edit className="w-4 h-4 text-blue-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(prayer.id)}
                    data-testid={`delete-prayer-${prayer.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
              
              {/* Content Preview */}
              <div className={`text-sm text-gray-600 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-3'}`}>
                {isExpanded ? prayer.content?.sv : contentPreview}
                {!isExpanded && hasMoreContent && '...'}
              </div>
              
              {/* Expand/Collapse Button */}
              {hasMoreContent && (
                <button
                  onClick={() => toggleExpanded(prayer.id)}
                  className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-3 h-3" />
                      Visa mindre
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" />
                      Visa mer
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderTabContent = (type) => {
    const filtered = getFilteredPrayers(type);
    const config = getTypeConfig(type);
    const hasImportablePrayers = COMMON_PRAYERS[type]?.length > 0;
    
    return (
      <div className="space-y-4">
        {/* Import suggestion for empty tabs */}
        {filtered.length === 0 && hasImportablePrayers && (
          <Card className="border-2 border-dashed border-blue-200 bg-blue-50/50">
            <CardContent className="p-6 text-center">
              <Download className="w-10 h-10 text-blue-400 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900 mb-1">
                Importera vanliga {config.label.toLowerCase()}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Vi har {COMMON_PRAYERS[type]?.length} fördefinierade böner redo att importera
              </p>
              <Button onClick={() => setIsImportOpen(true)} variant="outline" className="border-blue-300">
                <Download className="w-4 h-4 mr-2" />
                Öppna Importverktyg
              </Button>
            </CardContent>
          </Card>
        )}

        {filtered.length === 0 && !hasImportablePrayers ? (
          <Card className="border-dashed border-2 border-gray-200">
            <CardContent className="p-8 text-center">
              <config.icon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">Inga {config.label.toLowerCase()} ännu</p>
              <Button variant="outline" onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Lägg till första bönen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filtered.map(renderPrayerCard)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="prayers-manager" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Böner</h1>
          <p className="text-gray-600 mt-1">Hantera dagliga böner, påvens intentioner och rosenkransen</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsImportOpen(true)} data-testid="import-prayers-btn">
            <Download className="w-4 h-4 mr-2" />
            Importera
          </Button>
          <Button onClick={openCreateDialog} data-testid="create-prayer-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ny Bön
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {prayerTypes.map(type => {
          const count = getFilteredPrayers(type.value).length;
          const Icon = type.icon;
          return (
            <Card 
              key={type.value} 
              className={`border-0 shadow-md cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-${type.color}-50 to-white`}
              onClick={() => setActiveTab(type.value)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full bg-${type.color}-100 flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 text-${type.color}-600`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-500">{type.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          {prayerTypes.map(type => {
            const Icon = type.icon;
            const count = getFilteredPrayers(type.value).length;
            return (
              <TabsTrigger key={type.value} value={type.value} className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{type.label}</span>
                {count > 0 && (
                  <span className="hidden md:inline ml-1 px-1.5 py-0.5 text-xs bg-gray-200 rounded-full">
                    {count}
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
        
        {prayerTypes.map(type => (
          <TabsContent key={type.value} value={type.value}>
            {renderTabContent(type.value)}
          </TabsContent>
        ))}
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingPrayer ? (
                <>
                  <Edit className="w-5 h-5" />
                  Redigera Bön
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Ny Bön
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Prayer Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Typ av bön *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {prayerTypes.map(type => {
                  const Icon = type.icon;
                  const isSelected = formData.prayer_type === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, prayer_type: type.value }))}
                      className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                        isSelected 
                          ? `border-${type.color}-500 bg-${type.color}-50` 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? `text-${type.color}-600` : 'text-gray-400'}`} />
                      <span className={`text-xs ${isSelected ? 'font-medium' : 'text-gray-500'}`}>
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titel (Svenska) *
              </label>
              <Input
                value={formData.title.sv}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  title: { ...prev.title, sv: e.target.value } 
                }))}
                placeholder="T.ex. Fader Vår, Påvens intention för januari..."
                required
                data-testid="prayer-title-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Titeln översätts automatiskt till andra språk
              </p>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Innehåll (Svenska) *
              </label>
              <Textarea
                value={formData.content.sv}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  content: { ...prev.content, sv: e.target.value } 
                }))}
                placeholder="Skriv bönens text här..."
                rows={10}
                required
                className="font-mono text-sm"
                data-testid="prayer-content-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tips: Använd radbrytningar för att strukturera bönen
              </p>
            </div>

            {/* Date for Papal Intentions */}
            {formData.prayer_type === 'papal' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Månad och År *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={formData.date ? new Date(formData.date).getMonth() : ''}
                    onChange={(e) => {
                      const month = parseInt(e.target.value);
                      const year = formData.date ? new Date(formData.date).getFullYear() : new Date().getFullYear();
                      setFormData(prev => ({ 
                        ...prev, 
                        date: new Date(year, month, 1).toISOString().split('T')[0]
                      }));
                    }}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Välj månad...</option>
                    {monthNames.map((name, idx) => (
                      <option key={`month-${name}`} value={idx}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={formData.date ? new Date(formData.date).getFullYear() : ''}
                    onChange={(e) => {
                      const year = parseInt(e.target.value);
                      const month = formData.date ? new Date(formData.date).getMonth() : 0;
                      setFormData(prev => ({ 
                        ...prev, 
                        date: new Date(year, month, 1).toISOString().split('T')[0]
                      }));
                    }}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Välj år...</option>
                    {[2024, 2025, 2026, 2027, 2028].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Påvens böneintention för vald månad
                </p>
              </div>
            )}

            {/* Optional date for custom prayers */}
            {formData.prayer_type === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Datum (valfritt)
                </label>
                <Input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value || null }))}
                  data-testid="prayer-date-input"
                />
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Avbryt
              </Button>
              <Button type="submit" data-testid="save-prayer-btn">
                {editingPrayer ? 'Uppdatera' : 'Skapa'} Bön
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Förhandsgranskning
            </DialogTitle>
          </DialogHeader>
          
          {previewPrayer && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-lg">
                <h3 className="text-xl font-serif font-bold text-gray-900 mb-4">
                  {previewPrayer.title?.sv}
                </h3>
                <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {previewPrayer.content?.sv}
                </div>
                {previewPrayer.date && (
                  <p className="mt-4 text-sm text-gray-500">
                    {formatDate(previewPrayer.date)}
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500 text-center">
                Så här visas bönen för besökare på webbplatsen
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Importera Vanliga Böner
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <p className="text-sm text-gray-600">
              Välj böner att importera från vår samling av vanliga katolska böner.
            </p>

            {/* Daily Prayers */}
            {COMMON_PRAYERS.daily && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-blue-600" />
                    Dagliga Böner ({COMMON_PRAYERS.daily.length})
                  </h3>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleImportAll('daily')}
                  >
                    Importera alla
                  </Button>
                </div>
                <div className="grid gap-2">
                  {COMMON_PRAYERS.daily.map((prayer, idx) => (
                    <div key={`daily-${prayer.title?.sv || idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{prayer.title.sv}</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleImportPrayer(prayer, 'daily')}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rosary */}
            {COMMON_PRAYERS.rosary && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-600" />
                    Rosenkransen ({COMMON_PRAYERS.rosary.length})
                  </h3>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleImportAll('rosary')}
                  >
                    Importera alla
                  </Button>
                </div>
                <div className="grid gap-2">
                  {COMMON_PRAYERS.rosary.map((prayer, idx) => (
                    <div key={`rosary-${prayer.title?.sv || idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{prayer.title.sv}</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleImportPrayer(prayer, 'rosary')}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrayersManager;
